<template>
	<loader v-if="!formReady"/>
	<form v-else class="wide sections" @submit.prevent="submit">
		<section class="section--floating section--full">
			<fieldset>
				<div>
					<div>
						<legend><span>General</span></legend>
					</div>
					<div>
						<label
							v-if="!this.serviceProvider && $can('serviceProvider.updateTypeAttribute')"><span>Type </span>
							<div v-if="form.errors.get('type')" class="error-block">
								<p v-html="form.errors.get('type')"></p>
							</div>
							<select v-model="form.data.type">
								<option disabled selected value="">—</option>
								<option
									v-for="(label, type) in form.options.types" :value="type"
									v-html="label"></option>
							</select>
						</label>
						<label><span>Company Name </span>
							<div v-if="form.errors.get('profile.name')" class="error-block">
								<p v-html="form.errors.get('profile.name')"></p>
							</div>
							<div>
								<input
									v-model="form.data.profile.name" type="text"
									@keyup="event => setDefaults(event, form.data.profile.name)">
							</div>
						</label>
						<label><span>Company Short Name </span>
							<div v-if="form.errors.get('profile.short_name')" class="error-block">
								<p v-html="form.errors.get('profile.short_name')"></p>
							</div>
							<div>
								<input
									v-model="form.data.profile.short_name" type="text">
							</div>
							<div class="help-block">
								<p>The company short name is used in places where character length is limited, such as the sender name in SMS.</p>
							</div>
						</label>
						<label><span>URL Slug </span>
							<div v-if="form.errors.get('slug')" class="error-block">
								<p v-html="form.errors.get('slug')"></p>
							</div>
							<div v-if="slugEditable">
								<input v-model="form.data.slug" type="text">
							</div>
							<div class="help-block">
								<p>The URL slug allows us to use more user friendly names in URLs, for example, a law firm's client portal.</p>
								<p>Once set, it should rarely be changed as it may be in the public domain and any change will render shared URLs obsolete.</p>
								<template v-if="!slugEditable">
									<p>
										<button
											class="button button--secondary button--small"
											@click.prevent="editSlug = true">Change
										</button>
									</p>
								</template>
							</div>
						</label>
						<label><span>Address Name/Number and Street <span
							class="optional">— Optional </span></span>
							<div v-if="form.errors.get('profile.address_identifier')" class="error-block">
								<p v-html="form.errors.get('profile.address_identifier')"></p>
							</div>
							<div>
								<input v-model="form.data.profile.address_identifier" type="text">
							</div>
						</label>
						<label><span>Address Town/City <span class="optional">— Optional </span></span>
							<div>
								<input v-model="form.data.profile.address_locality" type="text">
							</div>
						</label>
						<label><span>Address County <span class="optional">— Optional </span></span>
							<div>
								<input v-model="form.data.profile.address_province" type="text">
							</div>
						</label>
						<label class="postcode"><span>Address Postcode <span
							v-if="'estateAgent' !== form.data.type" class="optional">— Optional </span></span>
							<div v-if="form.errors.get('profile.address_postcode')" class="error-block">
								<p v-html="form.errors.get('profile.address_postcode')"></p>
							</div>
							<div>
								<input v-model="form.data.profile.address_postcode" type="text">
							</div>
						</label>
						<label class="url"><span>Website <span class="optional">— Optional </span></span>
							<div
								v-if="form.errors.get('profile.website.protocol') || form.errors.get('profile.website.host')"
								class="error-block">
								<p v-html="form.errors.get('profile.website.protocol') || form.errors.get('profile.website.host')"></p>
							</div>
							<div>
								<select v-model="form.data.profile.website.protocol">
									<option disabled selected value="">—</option>
									<option value="http">http://</option>
									<option value="https">https://</option>
								</select>
								<input v-model="form.data.profile.website.host" type="text">
							</div>
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>Company Numbers</span></legend>
					</div>
					<div>
						<label><span>Registered Company Number <span class="optional">— Optional </span></span>
							<input v-model="form.data.profile.registered_number" type="text">
						</label>
						<label><span>VAT Number <span class="optional">— Optional </span></span>
							<input v-model="form.data.profile.vat_number" type="text">
						</label>
						<template v-if="showSolicitorConfig">
							<label><span>SRA Number <span class="optional">— Optional </span></span>
								<input v-model="form.data.config.sra_number" type="text">
							</label>
							<label><span>CQS Accreditation Number <span class="optional">— Optional </span></span>
								<input v-model="form.data.config.cqs_number" type="text">
							</label>
							<label><span>CLC Number <span class="optional">— Optional </span></span>
								<input v-model="form.data.config.clc_number" type="text">
							</label>
							<label><span>DX Number <span class="optional">— Optional </span></span>
								<input v-model="form.data.config.dx_number" type="text">
							</label>
							<label><span>Law Society Number <span class="optional">— Optional </span></span>
								<input v-model="form.data.config.law_society_number" type="text">
							</label>
							<label class="checkbox"><span>Lexcel Certified? </span>
								<input v-model="form.data.config.lexcel_certified" type="checkbox">
							</label>
						</template>
					</div>
				</div>
			</fieldset>
			<div class="actions">
				<ul>
					<li>
						<process-button
							:processing="form.processing" :text="$attrs.cta" class="button button--primary"/>
					</li>
				</ul>
			</div>
		</section>
		<template v-if="serviceProvider">
			<!--			status-->
			<section class="section--floating section--full">
				<fieldset>
					<div>
						<div>
							<legend><span>Status</span></legend>
						</div>
						<div>
							<label class="checkbox"><span>Signed SLA Received? </span>
								<input v-model="form.data.sla_received" type="checkbox">
							</label>
							<label
								class="checkbox"><span>Active? </span>
								<input
									v-model="form.data.active" type="checkbox">
							</label>
							<template v-if="showStatuses">
								<label class="checkbox"><span>Active For Introducers? </span>
									<input v-model="form.data.active_for_introducers" type="checkbox">
								</label>
							</template>
						</div>
					</div>
				</fieldset>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing" :text="$attrs.cta" class="button button--primary"/>
						</li>
					</ul>
				</div>
			</section>
			<!--			admin-->
			<section class="section--floating section--full">
				<fieldset>
					<div>
						<div>
							<legend><span>Administrator Details</span></legend>
						</div>
						<div>
							<label><span>Email </span>
								<div v-if="form.errors.get('admin.email')" class="error-block">
									<p v-html="form.errors.get('admin.email')"></p>
								</div>
								<div>
									<input v-model="form.data.admin.email" type="text">
								</div>
							</label>
							<label><span>First name </span>
								<div v-if="form.errors.get('admin.profile.name_first')" class="error-block">
									<p v-html="form.errors.get('admin.profile.name_first')"></p>
								</div>
								<div>
									<input v-model="form.data.admin.profile.name_first" type="text">
								</div>
							</label>
							<label><span>Last name </span>
								<div v-if="form.errors.get('admin.profile.name_last')" class="error-block">
									<p v-html="form.errors.get('admin.profile.name_last')"></p>
								</div>
								<div>
									<input v-model="form.data.admin.profile.name_last" type="text">
								</div>
							</label>
							<label><span>Telephone </span>
								<div v-if="form.errors.get('admin.profile.phone')" class="error-block">
									<p v-html="form.errors.get('admin.profile.phone')"></p>
								</div>
								<div>
									<input v-model="form.data.admin.profile.phone" type="text">
								</div>
							</label>
						</div>
					</div>
				</fieldset>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing" :text="$attrs.cta" class="button button--primary"/>
						</li>
					</ul>
				</div>
			</section>
			<!--			provided services-->
			<section v-if="showServicesConfig" class="section--floating section--full">
				<fieldset>
					<div>
						<div>
							<legend><span>Provided Services</span></legend>
							<div class="error-block">
								<p
									v-if="form.errors.get('provided_services')"
									v-html="form.errors.get('provided_services')"/>
							</div>
						</div>
						<div>
							<label><span>Services </span>
								<checkbox-manager
									:all-items="form.options.services"
									:checked="form.optionsByFieldKeys('services', 'provided_services')"
									helpBlock="Move the services that the server provider <em>can provide </em>into the right hand column or vice versa."
									itemLabel="names.plural"
									v-on:valuesChanged="(items) => setProvidedServices(items)"/>
							</label>
							<label
								class="checkbox"><span>Enable managed clients </span>
								<input v-model="form.data.managed_clients_enabled" type="checkbox">
							</label>
							<label
								class="checkbox"><span>Enable products </span>
								<input v-model="form.data.products_enabled" type="checkbox">
							</label>
						</div>
					</div>
				</fieldset>
				<fieldset v-if="$can('serviceProvider.configureIdChecks')">
					<div>
						<div>
							<legend><span>ID Checks</span></legend>
						</div>
						<div>
							<label
								class="checkbox"><span>Enable client ID checks </span>
								<input v-model="form.data.id_checks_enabled" type="checkbox">
							</label>
							<label
								class="checkbox"><span>Does the service provider pay for ID checks on behalf of the Client? </span>
								<input v-model="form.data.pays_for_id_checks" type="checkbox">
							</label>
						</div>
					</div>
				</fieldset>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing" :text="$attrs.cta" class="button button--primary"/>
						</li>
					</ul>
				</div>
			</section>
			<section
				v-for="(service, serviceIndex) in form.optionsByFieldKeys('services', 'provided_services')"
				v-if="showProvidedServicesConfiguration"
				:key="serviceIndex" class="section--floating section--full">
				<fieldset>
					<div v-if="form.data.provided_services[service.id]">
						<div>
							<legend><span>{{ service.names.plural }} Configuration</span></legend>
							<div class="error-block">
								<p
									v-if="form.errors.get('provided_services.' + service.id + '.commission_type')"
									v-html="form.errors.get('provided_services.' + service.id + '.commission_type')"/>
								<p
									v-if="form.errors.get('provided_services.' + service.id + '.commission_price')"
									v-html="form.errors.get('provided_services.' + service.id + '.commission_price')"/>
								<p
									v-if="form.errors.get('provided_services.' + service.id + '.commission_percent')"
									v-html="form.errors.get('provided_services.' + service.id + '.commission_percent')"/>
								<p
									v-if="form.errors.get('provided_services.' + service.id + '.publisher_commission_price')"
									v-html="form.errors.get('provided_services.' + service.id + '.publisher_commission_price')"/>
								<p
									v-if="form.errors.get('provided_services.' + service.id + '.publisher_commission_percent')"
									v-html="form.errors.get('provided_services.' + service.id + '.publisher_commission_percent')"/>
								<p
									v-if="form.errors.get('provided_services.' + service.id + '.maximum_monthly_quota')"
									v-html="form.errors.get('provided_services.' + service.id + '.maximum_monthly_quota')"/>
							</div>
						</div>
						<div>
							<label
								v-if="service.comparable"
								class="checkbox"><span>Receive leads for introduction comparisons </span>
								<input
									v-model="form.data.provided_services[service.id].disable_introduction_comparison_leads"
									type="checkbox">
								<div class="help-block">
									<p>This setting determines if the service provider would like a lead to be generated when an introducer creates a comparison that includes this service provider.</p>
								</div>
							</label>
							<label class="inline">
								<div class="inline__label">
									<span>Maximum Monthly Quota </span>
								</div>
								<div class="inline__input">
									<input
										v-model="form.data.provided_services[service.id].maximum_monthly_quota"
										class="number--millions"
										type="number">
								</div>
							</label>
							<section class="fieldset-section">
								<header>
									<h2>Payment On Account</h2>
								</header>
								<label class="inline">
									<div class="inline__label">
										<span>Required Payment On Account For Managed Clients <br/>(Fixed £) </span>
									</div>
									<div class="inline__input">
										<input
											v-model="form.data.provided_services[service.id].payment_on_account_for_managed_clients_price"
											class="number--hundreds"
											type="number">
									</div>
								</label>
							</section>
							<template v-if="service.extra_services.length">
								<section class="fieldset-section">
									<header>
										<h2>Extra Services</h2>
									</header>
									<label
										v-for="(extraService, extraServiceIndex) in service.extra_services"
										:key="extraServiceIndex" class="checkbox"><span
										v-html="$enum('ExtraServices').display(extraService)"/>
										<input
											v-model="form.data.provided_services[service.id].config.extra_services"
											:value="extraService"
											type="checkbox">
									</label>
								</section>
								<section
									v-for="(extraService, extraServiceIndex) in service.extra_services"
									v-if="form.fieldContainsValue('provided_services.' + service.id + '.config.extra_services', extraService)"
									:key="extraServiceIndex" class="fieldset-section">
									<!--										<template v-if="'welcomePack' === extraService">-->
									<!--											<header>-->
									<!--												<h2>Welcome Pack Details</h2>-->
									<!--											</header>-->
									<!--											<label><span>Welcome Pack URL? </span>-->
									<!--												<div-->
									<!--													class="error-block"-->
									<!--													v-if="form.errors.get('form.data.provided_services.' + service.id + '.config.welcome_pack_url')">-->
									<!--													<p v-html="form.errors.get('form.data.provided_services.' + service.id + '.config.welcome_pack_url')"></p>-->
									<!--												</div>-->
									<!--												<input-->
									<!--													type="text"-->
									<!--													v-model="form.data.provided_services[service.id].config.welcome_pack_url">-->
									<!--												<div class="help-block">-->
									<!--													<p>The welcome pack URL can be configured with the following placeholders:</p>-->
									<!--													<dl>-->
									<!--														<dt>Transaction type</dt>-->
									<!--														<dd v-html="'{{transaction}}'"></dd>-->
									<!--														<dt>Number of clients</dt>-->
									<!--														<dd v-html="'{{clients}}'"></dd>-->
									<!--														<dt>Client Reference</dt>-->
									<!--														<dd v-html="'{{client_ref}}'"></dd>-->
									<!--														<dt>Welcome pack ID</dt>-->
									<!--														<dd v-html="'{{id}}'"></dd>-->
									<!--													</dl>-->
									<!--												</div>-->
									<!--											</label>-->
									<!--										</template>-->
									<template v-if="'searchPack' === extraService">
										<header>
											<h2>Search Pack Details</h2>
										</header>
										<label><span>Applicable Search Pack Bundles </span>
											<div
												v-if="form.errors.get('form.data.provided_services.' + service.id + '.config.bundles')"
												class="error-block">
												<p v-html="form.errors.get('form.data.provided_services.' + service.id + '.config.bundles')"></p>
											</div>
											<checkbox-manager
												:all-items="form.options.bundles"
												:checked="form.optionsByFieldArray('bundles', 'provided_services.' + service.id + '.config.bundles')"
												itemLabel="name"
												v-on:valuesChanged="(items) => form.set('provided_services.' + service.id + '.config.bundles', items)"/>
										</label>
									</template>
								</section>
							</template>
						</div>
					</div>
				</fieldset>
				<template v-if="showConveyancingConfiguration(service)">
					<fieldset>
						<div>
							<div>
								<legend><span>Conveyancing Services</span></legend>
							</div>
							<div>
								<label class="checkbox"><span>Leasehold Transactions? </span>
									<input
										v-model="form.data.config.offers_leasehold_transactions"
										type="checkbox">
								</label>
								<label class="checkbox"><span>Islamic Mortgages? </span>
									<input
										v-model="form.data.config.offers_islamic_mortgage_transactions"
										type="checkbox">
								</label>
								<label class="checkbox"><span>New Build Conveyancing? </span>
									<input
										v-model="form.data.config.offers_new_build_transactions"
										type="checkbox">
								</label>
							</div>
						</div>
					</fieldset>
					<fieldset>
						<div>
							<div>
								<legend><span>Conveyancing Fees</span></legend>
							</div>
							<div>
								<div class="fee-fields">
									<label class="input-group"><span>Sale Transaction Value Fees </span>
										<div>
											<div
												v-for="(data, index) in form.data.fees.conveyancing.sale.fee_transaction_bands"
												class="input-grid--fees input-grid--bands">
												<label class="limit"><span>Up To (k) </span>
													<div
														v-if="index < form.data.fees.conveyancing.sale.fee_transaction_bands.length - 1">
														<input
															v-model="form.data.fees.conveyancing.sale.fee_transaction_bands[index].upto"
															step="1"
															type="number">
													</div>
												</label>
												<label class="fee"><span>Fee </span>
													<div>
														<input
															v-model="form.data.fees.conveyancing.sale.fee_transaction_bands[index].fee"
															step="1"
															type="number">
													</div>
												</label>
												<label class="repeater-controls">
													<button
														class="repeater-controls__add"
														@click.prevent="addFeeBand('sale', index)">Add
													</button>
													<button
														class="repeater-controls__remove"
														@click.prevent="removeFeeBand('sale', index)">Remove
													</button>
												</label>
											</div>
										</div>
									</label>
									<label class="input-group"><span>Purchase Transaction Value Fees </span>
										<div>
											<div
												v-for="(data, index) in form.data.fees.conveyancing.purchase.fee_transaction_bands"
												class="input-grid--fees input-grid--bands">
												<label class="limit"><span>Up To (k) </span>
													<div
														v-if="index < form.data.fees.conveyancing.purchase.fee_transaction_bands.length - 1">
														<input
															v-model="form.data.fees.conveyancing.purchase.fee_transaction_bands[index].upto"
															step="1"
															type="number">
													</div>
												</label>
												<label class="fee"><span>Fee </span>
													<div>
														<input
															v-model="form.data.fees.conveyancing.purchase.fee_transaction_bands[index].fee"
															step="1"
															type="number">
													</div>
												</label>
												<label class="repeater-controls">
													<button
														class="repeater-controls__add"
														@click.prevent="addFeeBand('purchase', index)">Add
													</button>
													<button
														class="repeater-controls__remove"
														@click.prevent="removeFeeBand('purchase', index)">Remove
													</button>
												</label>
											</div>
										</div>
									</label>
									<label
										class="input-group"><span>Remortgage Transaction Value Fees </span>
										<div>
											<div
												v-for="(data, index) in form.data.fees.conveyancing.remortgage.fee_transaction_bands"
												class="input-grid--fees input-grid--bands">
												<label
													class="limit"><span>Up To (k) </span>
													<div
														v-if="index < form.data.fees.conveyancing.remortgage.fee_transaction_bands.length - 1">
														<input
															v-model="form.data.fees.conveyancing.remortgage.fee_transaction_bands[index].upto"
															step="1"
															type="number">
													</div>
												</label>
												<label
													class="fee"><span>Fee </span>
													<div>
														<input
															v-model="form.data.fees.conveyancing.remortgage.fee_transaction_bands[index].fee"
															step="1"
															type="number">
													</div>
												</label>
												<label class="repeater-controls">
													<button
														class="repeater-controls__add"
														@click.prevent="addFeeBand('remortgage', index)">Add
													</button>
													<button
														class="repeater-controls__remove"
														@click.prevent="removeFeeBand('remortgage', index)">Remove
													</button>
												</label>
											</div>
										</div>
									</label>
								</div>
								<div class="fee-fields">
									<label class="input-group"><span>Telegraphic Transfer Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_telegraphic_transfer"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_telegraphic_transfer"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_telegraphic_transfer"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										v-if="form.data.config.offers_leasehold_transactions"
										class="input-group"><span>Leasehold Property Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_leasehold_property"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_leasehold_property"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_leasehold_property"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										v-if="form.data.config.offers_islamic_mortgage_transactions"
										class="input-group"><span>Islamic Mortgage Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_islamic_mortgage"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_islamic_mortgage"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_islamic_mortgage"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										v-if="form.data.config.offers_new_build_transactions"
										class="input-group"><span>New Build Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_new_build"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Shared Ownership Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_shared_ownership"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_shared_ownership"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_shared_ownership"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Right To Buy Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_right_to_buy"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Transfer Of Equity Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_transfer_of_equity"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_transfer_of_equity"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_transfer_of_equity"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Auction/Repossession Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_auction_repossession"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_auction_repossession"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Help To Buy Scheme Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_help_to_buy_scheme"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_help_to_buy_scheme"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Help To Buy ISA Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_help_to_buy_isa"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_help_to_buy_isa"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Stamp Duty Form Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_stamp_duty_form"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Mortgage Admin Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_mortgage_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_mortgage_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Lawyer Checker Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_lawyer_checker"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_lawyer_checker"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_lawyer_checker"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>File Admin Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_file_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_file_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_file_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Lender Monitor Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_lender_monitor"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_lender_monitor"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_lender_monitor"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
								</div>
							</div>
						</div>
					</fieldset>
					<fieldset>
						<div>
							<div>
								<legend><span>Conveyancing Disbursements</span></legend>
							</div>
							<div>
								<label><span>Private Search Pack Fee </span>
									<input
										v-model="form.data.config.private_search_pack_fee" min="0.01" step="0.01"
										type="number"/>
								</label>
								<label><span>Wholesale ID Check Fee </span>
									<input
										v-model="form.data.config.wholesale_id_check_fee" min="0.01" step="0.01"
										type="number"/>
								</label>
								<div class="fee-fields">
									<label class="input-group"><span>Search Pack </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.disbursement_search_pack"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Bankruptcy Search Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.disbursement_bankruptcy_search"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_bankruptcy_search"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>ID Check Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.disbursement_id_check"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.disbursement_id_check"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_id_check"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Office Copies & Title Plan Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.disbursement_office_copies_title_plan"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_office_copies_title_plan"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										class="input-group"><span>Land Registry Pre Completion Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.disbursement_land_registry_pre_completion"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_land_registry_pre_completion"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										class="input-group"><span>Remortgage No Search Insurance Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_remortgage_search_policy"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<div class="fieldset-section">
										<header>
											<h2>Other Disbursements</h2>
										</header>
										<label class="inline"><span>ID Check Fee</span>
											<input
												v-model="form.data.fees.conveyancing.other.disbursement_id_check"
												class="number--hundreds" min="0.01" step="0.01"
												type="number"/>
										</label>
									</div>
								</div>
							</div>
						</div>
					</fieldset>
					<fieldset>
						<div>
							<div>
								<legend><span>Lenders</span></legend>
							</div>
							<div>
								<label class="checkbox"><span>Lenders </span>
									<checkbox-manager
										:all-items="form.options.lenders"
										:checked="form.optionsByFieldArray('lenders', 'lenders')"
										helpBlock="Move the lenders that the solicitor <em>does work with </em>into the right hand column or vice versa."
										itemLabel="name"
										v-on:valuesChanged="(items) => form.set('lenders', items)"/>
								</label>
							</div>
						</div>
					</fieldset>
				</template>
				<template v-if="showPropertyListingConfiguration(service)">
					<fieldset>
						<div>
							<div>
								<legend><span>Areas</span></legend>
							</div>
							<div>
								<label><span>Manually Assigned Postcodes </span>
									<v-tags
										:datasets="postcodeTypeaheadData"
										:tag-label="option => option.display"
										:tags="linkedGeometries"
										v-on:tag-added="postcodeTagAdded"
										v-on:tag-deleted="postcodeTagDeleted"/>
								</label>
							</div>
						</div>
					</fieldset>
				</template>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing" :text="$attrs.cta" class="button button--primary"/>
						</li>
					</ul>
				</div>
			</section>
			<section v-if="showIntroducerConfig" class="section--floating section--full">
				<fieldset>
					<div>
						<div>
							<legend><span>Introduced Services</span></legend>
						</div>
						<div>
							<label class="checkbox"><span>Is this service provider an introducer? </span>
								<div>
									<input
										v-model="form.data.introducer" type="checkbox"
										@change="resetPrivateIntroducerValues()">
								</div>
							</label>
							<template v-if="showIntroducerSettings">
								<label><span>Services </span>
									<checkbox-manager
										:all-items="introduceableServices"
										:checked="form.optionsByFieldKeys('services', 'introduced_services')"
										helpBlock="Move the services that the server provider <em>can provide </em>into the right hand column or vice versa."
										itemLabel="names.plural"
										v-on:valuesChanged="(items) => form.setKeys('introduced_services', items)"/>
								</label>
								<label><span>Commission band </span>
									<select v-model="form.data.introducer_commission_band">
										<option :value="null" disabled selected>—</option>
										<option value="bronze">Bronze</option>
										<option value="silver">Silver</option>
										<option value="gold">Gold</option>
									</select>
									<div v-if="form.errors.get('introducer_commission_band')" class="error-block">
										<p v-html="form.errors.get('introducer_commission_band')"/>
									</div>
								</label>
								<label class="inline">
									<div class="inline__label">
										<span>Commission percentage override </span>
									</div>
									<div class="inline__input">
										<input
											v-model="form.data.introducer_commission_percent" class="number--hundreds"
											type="number">
									</div>
									<div v-if="form.errors.get('introducer_commission_percent')" class="error-block">
										<p v-html="form.errors.get('introducer_commission_percent')"/>
									</div>
								</label>
							</template>
						</div>
					</div>
				</fieldset>
				<fieldset v-if="introducedServices.length">
					<table>
						<thead>
							<tr>
								<th align="left">Service</th>
								<th align="left">Panel</th>
								<th align="right" class="discreet"><span>Actions</span></th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(service, serviceIndex) in introducedServices"
								:key="serviceIndex">
								<td align="left" v-html="service.names.plural"/>
								<td
									align="left"
									v-html="panelName(form.data.introduced_services[service.id].panel_id)"/>
								<td align="right">
									<v-contextual>
										<v-contextual-option
											@click.prevent="showIntroducedServiceModal(service)">Configure</v-contextual-option>
									</v-contextual>
								</td>
							</tr>
						</tbody>
					</table>
				</fieldset>
			</section>
			<!--			<section-->
			<!--				v-if="showIntroducerSettings"-->
			<!--				v-for="(service, serviceIndex) in form.optionsByFieldKeys('services', 'introduced_services')"-->
			<!--				:key="serviceIndex"-->
			<!--				class="section&#45;&#45;floating section&#45;&#45;full">-->
			<!--				<fieldset>-->
			<!--					<div v-if="form.data.introduced_services[service.id]">-->
			<!--						<div>-->
			<!--							<legend><span v-html="service.names.plural + ' Configuration'"></span></legend>-->
			<!--						</div>-->
			<!--						<div>-->
			<!--							<label><span>Panel </span>-->
			<!--								<div v-if="!form.options.panels.length" class="nothing-to-see-here">-->
			<!--									<p>No available panels.</p>-->
			<!--									<router-link-->
			<!--										class="button button&#45;&#45;text"-->
			<!--										:to="{name: 'panels.create'}">Create A Panel</router-link>-->
			<!--								</div>-->
			<!--								<select v-else v-model="form.data.introduced_services[service.id].panel_id">-->
			<!--									<option :value="null" selected disabled>—</option>-->
			<!--									<option-->
			<!--										v-for="(panel, panelIndex) in form.options.panels"-->
			<!--										:key="panelIndex"-->
			<!--										:value="panel.id"-->
			<!--										v-html="panel.name"/>-->
			<!--								</select>-->
			<!--							</label>-->
			<!--							&lt;!&ndash;							<template v-if="!form.data.introduced_services[service.id].panel_id">&ndash;&gt;-->
			<!--							&lt;!&ndash;								<label class="checkbox"><span>Use Campaign Configuration </span>&ndash;&gt;-->
			<!--							&lt;!&ndash;									<input&ndash;&gt;-->
			<!--							&lt;!&ndash;										type="checkbox"&ndash;&gt;-->
			<!--							&lt;!&ndash;										v-model="form.data.introduced_services[service.id].use_campaign">&ndash;&gt;-->
			<!--							&lt;!&ndash;									<div class="help-block">&ndash;&gt;-->
			<!--							&lt;!&ndash;										<p>This setting determines if configuration for this service is based on a campaign.</p>&ndash;&gt;-->
			<!--							&lt;!&ndash;									</div>&ndash;&gt;-->
			<!--							&lt;!&ndash;								</label>&ndash;&gt;-->
			<!--							&lt;!&ndash;								<template v-if="!form.data.introduced_services[service.id].use_campaign">&ndash;&gt;-->
			<!--							&lt;!&ndash;									<label&ndash;&gt;-->
			<!--							&lt;!&ndash;										v-if="service.force_variable_introducer_commissions || (service.variable_introducer_commissions && form.data.introduced_services[service.id].variable_commissions)">&ndash;&gt;-->
			<!--							&lt;!&ndash;										<span>Default Introducer Commission Fee For New Clients (Fixed £) </span>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<input&ndash;&gt;-->
			<!--							&lt;!&ndash;											type="number"&ndash;&gt;-->
			<!--							&lt;!&ndash;											v-model="form.data.introduced_services[service.id].commission_price">&ndash;&gt;-->
			<!--							&lt;!&ndash;										<div class="help-block">&ndash;&gt;-->
			<!--							&lt;!&ndash;											<p>This setting determines the fixed instruction commission fee that the introducer will be owed and is paid by the instruction recipient in addition to the Calm instruction fee.</p>&ndash;&gt;-->
			<!--							&lt;!&ndash;										</div>&ndash;&gt;-->
			<!--							&lt;!&ndash;									</label>&ndash;&gt;-->
			<!--							&lt;!&ndash;									<label v-else>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<span>Introducer Commission Fee For New Clients (Percent %) </span>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<input&ndash;&gt;-->
			<!--							&lt;!&ndash;											type="number"&ndash;&gt;-->
			<!--							&lt;!&ndash;											v-model="form.data.introduced_services[service.id].commission_percent">&ndash;&gt;-->
			<!--							&lt;!&ndash;										<div class="help-block">&ndash;&gt;-->
			<!--							&lt;!&ndash;											<p>This setting determines the instruction commission fee that the introducer will be owed as a percentage of the Calm instruction fee and is paid out of the Calm instruction fee.</p>&ndash;&gt;-->
			<!--							&lt;!&ndash;										</div>&ndash;&gt;-->
			<!--							&lt;!&ndash;									</label>&ndash;&gt;-->
			<!--							&lt;!&ndash;									<label&ndash;&gt;-->
			<!--							&lt;!&ndash;										v-if="service.force_variable_introducer_commissions || (service.variable_introducer_commissions && form.data.introduced_services[service.id].variable_commissions)">&ndash;&gt;-->
			<!--							&lt;!&ndash;										<span>Default Introducer Commission Fee For Existing Clients (Fixed £) </span>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<input&ndash;&gt;-->
			<!--							&lt;!&ndash;											type="number"&ndash;&gt;-->
			<!--							&lt;!&ndash;											v-model="form.data.introduced_services[service.id].commission_price_cross_sell">&ndash;&gt;-->
			<!--							&lt;!&ndash;										<div class="help-block">&ndash;&gt;-->
			<!--							&lt;!&ndash;											<p>This setting determines the fixed instruction commission fee that the introducer will be owed and is paid by the instruction recipient in addition to the Calm instruction fee.</p>&ndash;&gt;-->
			<!--							&lt;!&ndash;										</div>&ndash;&gt;-->
			<!--							&lt;!&ndash;									</label>&ndash;&gt;-->
			<!--							&lt;!&ndash;									<label v-else>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<span>Introducer Commission Fee For Existing Clients (Percent %) </span>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<input&ndash;&gt;-->
			<!--							&lt;!&ndash;											type="number"&ndash;&gt;-->
			<!--							&lt;!&ndash;											v-model="form.data.introduced_services[service.id].commission_percent_cross_sell">&ndash;&gt;-->
			<!--							&lt;!&ndash;										<div class="help-block">&ndash;&gt;-->
			<!--							&lt;!&ndash;											<p>This setting determines the instruction commission fee that the introducer will be owed as a percentage of the Calm instruction fee and is paid out of the Calm instruction fee.</p>&ndash;&gt;-->
			<!--							&lt;!&ndash;										</div>&ndash;&gt;-->
			<!--							&lt;!&ndash;									</label>&ndash;&gt;-->
			<!--							&lt;!&ndash;									<label><span&ndash;&gt;-->
			<!--							&lt;!&ndash;										v-html="'conveyancing' === service.key && !form.data.private_introducer ? 'Conveyancing Panel ' : 'Recipient '"></span>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<div&ndash;&gt;-->
			<!--							&lt;!&ndash;											class="nothing-to-see-here"&ndash;&gt;-->
			<!--							&lt;!&ndash;											v-if="!viableVendors(service).length">&ndash;&gt;-->
			<!--							&lt;!&ndash;											<p>No service providers currently offering this service</p>&ndash;&gt;-->
			<!--							&lt;!&ndash;										</div>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<template v-if="viableVendors(service).length">&ndash;&gt;-->
			<!--							&lt;!&ndash;											<checkbox-manager&ndash;&gt;-->
			<!--							&lt;!&ndash;												v-if="'conveyancing' === service.key && !form.data.private_introducer"&ndash;&gt;-->
			<!--							&lt;!&ndash;												:all-items="viableVendors(service)"&ndash;&gt;-->
			<!--							&lt;!&ndash;												:checked="checkedConveyancers"&ndash;&gt;-->
			<!--							&lt;!&ndash;												itemLabel="profile.name"&ndash;&gt;-->
			<!--							&lt;!&ndash;												helpBlock="Move the solicitors that <em>will be compared </em>into the right hand column or vice versa."&ndash;&gt;-->
			<!--							&lt;!&ndash;												v-on:valuesChanged="(items) => form.set('introduced_services.' + service.id + '.providers', items)"/>&ndash;&gt;-->
			<!--							&lt;!&ndash;											<select&ndash;&gt;-->
			<!--							&lt;!&ndash;												v-else&ndash;&gt;-->
			<!--							&lt;!&ndash;												v-model="form.data.introduced_services[service.id].providers">&ndash;&gt;-->
			<!--							&lt;!&ndash;												<option value="" disabled selected>—</option>&ndash;&gt;-->
			<!--							&lt;!&ndash;												<option&ndash;&gt;-->
			<!--							&lt;!&ndash;													v-for="(provider, providerIndex) in viableVendors(service)"&ndash;&gt;-->
			<!--							&lt;!&ndash;													:key="providerIndex"&ndash;&gt;-->
			<!--							&lt;!&ndash;													:value="provider.id"&ndash;&gt;-->
			<!--							&lt;!&ndash;													v-html="provider.profile.name"></option>&ndash;&gt;-->
			<!--							&lt;!&ndash;											</select>&ndash;&gt;-->
			<!--							&lt;!&ndash;										</template>&ndash;&gt;-->
			<!--							&lt;!&ndash;									</label>&ndash;&gt;-->
			<!--							&lt;!&ndash;								</template>&ndash;&gt;-->
			<!--							&lt;!&ndash;								<template v-if="form.data.introduced_services[service.id].use_campaign">&ndash;&gt;-->
			<!--							&lt;!&ndash;									<label><span>Campaign </span>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<div&ndash;&gt;-->
			<!--							&lt;!&ndash;											class="nothing-to-see-here" v-if="!viableCampaigns(service).length">&ndash;&gt;-->
			<!--							&lt;!&ndash;											<div class="editorial">&ndash;&gt;-->
			<!--							&lt;!&ndash;												<p>No campaigns for this introducer/service configuration</p>&ndash;&gt;-->
			<!--							&lt;!&ndash;												<router-link&ndash;&gt;-->
			<!--							&lt;!&ndash;													:to="{name: 'campaigns.create'}"&ndash;&gt;-->
			<!--							&lt;!&ndash;													v-if="$can('createCampaigns')">Create a campaign</router-link>&ndash;&gt;-->
			<!--							&lt;!&ndash;											</div>&ndash;&gt;-->
			<!--							&lt;!&ndash;										</div>&ndash;&gt;-->
			<!--							&lt;!&ndash;										<select&ndash;&gt;-->
			<!--							&lt;!&ndash;											v-model="form.data.introduced_services[service.id].campaign_id"&ndash;&gt;-->
			<!--							&lt;!&ndash;											v-if="viableCampaigns(service).length">&ndash;&gt;-->
			<!--							&lt;!&ndash;											<option value="" selected>—</option>&ndash;&gt;-->
			<!--							&lt;!&ndash;											<option&ndash;&gt;-->
			<!--							&lt;!&ndash;												v-for="(campaign, campaignIndex) in viableCampaigns(service)"&ndash;&gt;-->
			<!--							&lt;!&ndash;												:value="campaign.id" v-html="campaign.name"></option>&ndash;&gt;-->
			<!--							&lt;!&ndash;										</select>&ndash;&gt;-->
			<!--							&lt;!&ndash;									</label>&ndash;&gt;-->
			<!--							&lt;!&ndash;								</template>&ndash;&gt;-->
			<!--							&lt;!&ndash;							</template>&ndash;&gt;-->
			<!--						</div>-->
			<!--					</div>-->
			<!--				</fieldset>-->
			<!--				<div class="actions">-->
			<!--					<ul>-->
			<!--						<li>-->
			<!--							<process-button-->
			<!--								class="button button&#45;&#45;primary" :processing="form.processing" :text="$attrs.cta"/>-->
			<!--						</li>-->
			<!--					</ul>-->
			<!--				</div>-->
			<!--			</section>-->
		</template>
	</form>
</template>
<script>
import {createNamespacedHelpers} from "vuex";

const {mapActions} = createNamespacedHelpers("serviceProviders");

export default {

	data() {
		return {
			formReady: false,
			editSlug: false,
			form: this.newForm({
				type: "",
				group: null,
				slug: "",
				introducer: false,
				private_introducer: false,
				mask_name: false,
				assume_opt_in_for_introductions: false,
				allow_campaign_selection: false,
				introducer_commission_band: null,
				introducer_commission_percent: null,
				sla_received: false,
				terms_agreed: false,
				active: false,
				active_for_introducers: false,
				profile: {
					name: null,
					short_name: null,
					address_identifier: null,
					address_locality: null,
					address_province: null,
					address_postcode: null,
					website: {
						protocol: "",
						host: null,
					},
					registered_number: null,
					vat_number: null,
				},
				admin: {
					email: null,
					profile: {
						name_first: null,
						name_last: null,
						phone: null,
					},
				},
				provided_services: [
					{
						commission_type: null,
						commission_price: null,
						commission_percent: null,
						publisher_commission_price: null,
						publisher_commission_percent: null,
						disable_introduction_comparison_leads: false,
						maximum_monthly_quota: null,
						payment_on_account_for_managed_clients_price: null,
						config: {
							extra_services: [],
							order_search_packs_direct: false,
							welcome_pack_url: "",
							bundles: [],
						},
					},
				],
				id_checks_enabled: false,
				pays_for_id_checks: false,
				managed_clients_enabled: false,
				products_enabled: false,
				introduced_services: [
					{
						panel_id: null,
					},
				],
				config: {
					sra_number: null,
					cqs_number: null,
					clc_number: null,
					dx_number: null,
					law_society_number: null,
					lexcel_certified: false,
				},
				fees: {
					conveyancing: {
						sale: {
							fee_transaction_bands: [{
								upto: null,
								fee: null,
							}],
							fee_telegraphic_transfer: null,
							fee_leasehold_property: null,
							fee_islamic_mortgage: null,
							fee_new_build: null,
							fee_shared_ownership: null,
							fee_right_to_buy: null,
							fee_transfer_of_equity: null,
							fee_auction_repossession: null,
							fee_help_to_buy_scheme: null,
							fee_help_to_buy_isa: null,
							fee_stamp_duty_form: null,
							fee_mortgage_admin: null,
							fee_lawyer_checker: null,
							fee_buy_to_let: null,
							fee_file_admin: null,
							fee_lender_monitor: null,
							disbursement_search_pack: null,
							disbursement_bankruptcy_search: null,
							disbursement_office_copies_title_plan: null,
							disbursement_land_registry_pre_completion: null,
							disbursement_remortgage_search_policy: null,
							// remove
							disbursement_id_check: null,
						},
						purchase: {
							fee_transaction_bands: [{
								upto: null,
								fee: null,
							}],
							fee_telegraphic_transfer: null,
							fee_leasehold_property: null,
							fee_islamic_mortgage: null,
							fee_new_build: null,
							fee_shared_ownership: null,
							fee_right_to_buy: null,
							fee_transfer_of_equity: null,
							fee_auction_repossession: null,
							fee_help_to_buy_scheme: null,
							fee_help_to_buy_isa: null,
							fee_stamp_duty_form: null,
							fee_mortgage_admin: null,
							fee_lawyer_checker: null,
							fee_buy_to_let: null,
							disbursement_search_pack: null,
							disbursement_bankruptcy_search: null,
							disbursement_office_copies_title_plan: null,
							disbursement_land_registry_pre_completion: null,
							disbursement_remortgage_search_policy: null,
							// remove
							disbursement_id_check: null,
						},
						remortgage: {
							fee_transaction_bands: [{
								upto: null,
								fee: null,
							}],
							fee_telegraphic_transfer: null,
							fee_leasehold_property: null,
							fee_islamic_mortgage: null,
							fee_new_build: null,
							fee_shared_ownership: null,
							fee_right_to_buy: null,
							fee_transfer_of_equity: null,
							fee_auction_repossession: null,
							fee_help_to_buy_scheme: null,
							fee_help_to_buy_isa: null,
							fee_stamp_duty_form: null,
							fee_mortgage_admin: null,
							fee_lawyer_checker: null,
							fee_buy_to_let: null,
							disbursement_search_pack: null,
							disbursement_bankruptcy_search: null,
							disbursement_office_copies_title_plan: null,
							disbursement_land_registry_pre_completion: null,
							disbursement_remortgage_search_policy: null,
							// remove
							disbursement_id_check: null,
						},
						other: {
							fee_transaction_bands: [{
								upto: null,
								fee: null,
							}],
							disbursement_id_check: null,
						},
					},
				},
				lenders: [],
				linked_postcode_geometries: [],
			}),
		};
	},

	props: {
		serviceProvider: null,
		method: {
			type: String,
			default() {
				return "post";
			},
		},
	},

	computed: {
		slugEditable() {
			return "post" === this.method || this.editSlug || (this.serviceProvider && !this.serviceProvider.slug);
		},
		group() {
			return _.find(this.form.options.groups, function (group) {
				return group.id === this.form.data.group;
			}.bind(this));
		},
		conveyancingService() {
			return _.find(this.form.options.services, function (service) {
				return "conveyancing" === service.key;
			});
		},
		propertyListingService() {
			return _.find(this.form.options.services, function (service) {
				return "propertyListing" === service.key;
			});
		},
		showStatuses() {
			return ["introducer", "publisher"].indexOf(this.form.data.type) === -1;
		},
		showSolicitorConfig() {
			return "solicitor" === this.form.data.type;
		},
		showServicesConfig() {
			return this.$can("serviceProvider.manageServices") && _.indexOf(this.form.options.serviceConfigurableTypes, this.form.data.type) > -1;
		},
		showProvidedServicesConfiguration() {
			return _.indexOf(this.form.options.commissionConfigurableTypes, this.form.data.type) > -1;
		},
		showIntroducerConfig() {
			return this.$can("serviceProvider.manageServices") && _.indexOf(this.form.options.introduceableTypes, this.form.data.type) > -1;
		},
		showIntroducerSettings() {
			return this.form.data.introducer;
		},
		introduceableServices() {
			let services = this.form.data.introductions_recipient
				? this.form.options.vendorServiceProviders[this.form.data.introductions_recipient].providedServices
				: this.form.options.services;
			let serviceIds = _.values(_.mapValues(services, "id"));

			return _.filter(this.form.options.services, function (service) {
				return _.indexOf(serviceIds, service.id) > -1;
			}.bind(this));
		},
		introducedServices() {
			return this.form.optionsByFieldKeys("services", "introduced_services");
		},
		checkedConveyancers() {
			let conveyancing = _.find(this.form.options.services, function (service) {
				return "conveyancing" === service.key;
			});
			let data = this.form.getData("introduced_services." + conveyancing.id + ".providers"); // array

			return _.filter(this.viableVendors(conveyancing), function (conveyancer) {
				return data.indexOf(conveyancer.id) > -1;
			});
		},
		showPublisherConfig() {
			return "publisher" === this.form.data.type;
		},
		showPublisherFees() {
			return _.has(this.form.data.published_services, this.conveyancingService.id) && this.form.data.published_services[this.conveyancingService.id].single_quote; // true; // this.form.data.published_services"publisher" === this.form.data.type;
		},
		publishableServices() {
			return _.filter(this.form.options.services, function (service) {
				return service.comparable;
			});
		},
		checkedPublisherConveyancers() {
			let data = this.form.getData("published_services." + this.conveyancingService.id + ".providers") || [];
			return _.filter(this.viableVendors(this.conveyancingService), function (serviceProvider) {
				return data.indexOf(serviceProvider.id) > -1;
			});
		},
		linkedGeometries() {
			return {};
			let data = this.form.getOption("postcodeTypeahead");
			let outwardsIndex = _.findIndex(data, function (object) {
				return "outwards" === object.name;
			});

			return _.filter(data[outwardsIndex].source, function (outward) {
				return this.form.data.linked_postcode_geometries.indexOf(outward.identify) > -1;
			}.bind(this));
		},
		postcodeTypeaheadData() {
			return {};
			let data = this.form.getOption("postcodeTypeahead");
			let outwardsIndex = _.findIndex(data, function (object) {
				return "outwards" === object.name;
			});

			_.set(data, outwardsIndex + ".source", _.filter(data[outwardsIndex].source, function (outward) {
				return this.form.data.linked_postcode_geometries.indexOf(outward.identify) < 0;
			}.bind(this)));

			return data;
		},
	},

	methods: {
		...mapActions([
			"addModel",
			"updateModel",
		]),
		setDefaults(event, name, force = false) {
			if (["ArrowRight", "ArrowLeft"].indexOf(event.key) > -1) {
				return;
			}
			this.setShortName(name);
			this.setSlug(name, force);
		},
		setShortName(name) {
			this.form.set("profile.short_name", name.replace(/[^a-zA-Z]/g, ""));
		},
		setSlug(name, force = false) {
			if ("post" !== this.method && !force) {
				return;
			}
			this.form.set("slug", name.trim(" ").replace(/\s+/g, "-").replace(/&/g, "and").replace(/\'/g, "").toLowerCase());
		},
		groupChanged() {
			if (!this.group) {
				return;
			}

			if (!this.group.config.disable_users) {
				return;
			}

			this.form.set("active", true);
		},
		// provided services
		setProvidedServices(items) {
			let self = this;
			this.form.setKeys("provided_services", items);

			_.each(items, function (serviceId) {
				let service = _.find(self.form.options.services, function (service) {
					return service.id === serviceId;
				});
				let config = self.getProvidedServiceConfig(service);

				self.form.set("provided_services." + serviceId, config);
			});
		},
		getProvidedServiceConfig(service) {
			let config = this.form.getData("provided_services", service.id);

			if (!this.serviceProvider) {
				return config;
			}

			let existingConfig = _.has(this.serviceProvider.providedServices, service.id)
				? this.serviceProvider.providedServices[service.id].pivot
				: null;

			if (!existingConfig) {
				return config;
			}

			let dataManipulations = {
				commission_price: existingConfig.commission_price?.number.wholes || "",
				commission_percent: existingConfig.commission_percent?.number.wholes || "",
				publisher_commission_price: existingConfig.publisher_commission_price?.number.wholes || "",
				publisher_commission_percent: existingConfig.publisher_commission_percent?.number.wholes || "",
				payment_on_account_for_managed_clients_price: existingConfig.payment_on_account_for_managed_clients_price?.number.wholes || "",
				config: _.merge(config.config, _.mapValues(existingConfig.config, function (value, key) {
					if ("bundles" === key) {
						return _.map(value, bundle => bundle.id);
					}

					return value;
				})),
			};

			_.each(config, function (value, key) {
				config[key] = _.has(dataManipulations, key) ? dataManipulations[key] : (
					existingConfig ? existingConfig[key] : config[key]
				);
			});

			return config;
		},
		viableVendors(service) {
			let serviceId = service.id;
			return _.filter(this.form.options.vendorServiceProviders, function (provider) {
				return _.find(provider.providedServices, function (service) {
					return service.id === serviceId;
				});
			});
		},
		showConveyancingConfiguration(service) {
			return this.conveyancingService && this.conveyancingService.id === service.id;
		},
		addFeeBand(type, index) {
			this.form.pushDefault("fees.conveyancing." + type + ".fee_transaction_bands", index);
		},
		removeFeeBand(type, index) {
			this.form.removeIndex("fees.conveyancing." + type + ".fee_transaction_bands", index);
		},

		showPropertyListingConfiguration(service) {
			return this.propertyListingService && this.propertyListingService.id === service.id;
		},
		postcodeTagAdded(tag, tags) {
			let data = this.form.getData("linked_postcode_geometries");

			_.each(tag.outwards, function (outward) {
				data.push(outward);
			});

			this.form.set("linked_postcode_geometries", _.uniq(data));
		},
		postcodeTagDeleted(index, tag, tags) {
			let data = this.form.getData("linked_postcode_geometries");

			_.pullAll(data, tag.outwards);

			this.form.set("linked_postcode_geometries", _.uniq(data));
		},

		// introduced services
		resetPrivateIntroducerValues() {
			this.form.reset("private_introducer");
		},
		getIntroducedServiceConfig(service) {
			let config = this.form.getData("introduced_services", service.id);

			if (!this.serviceProvider) {
				return config;
			}

			let existingConfig = _.has(this.serviceProvider.referredServices, service.id)
				? this.serviceProvider.referredServices[service.id].pivot
				: null;

			return {
				panel_id: existingConfig.panel?.id || null,
			};
			console.log("existingConfig", existingConfig);
			if (!existingConfig) {
				return config;
			}

			_.each(config, function (value, key) {
				let dataManipulations = {
					panel_id: existingConfig.panel?.id || null,
					// providers: service.comparable
					// 	? _.map(existingConfig.serviceProviders, "id")
					// 	: (existingConfig.serviceProviders.length ? existingConfig.serviceProviders[0].id : null),
				};

				config[key] = _.has(dataManipulations, key) ? dataManipulations[key] : existingConfig[key];
			});
			console.log("final", config);
			return config;
		},
		panelName(panelId) {
			return _.find(this.form.options.panels, ["id", panelId])?.name || "—";
		},
		showIntroducedServiceModal(service) {
			this.$vmodal.open({
				component: require("../modals/IntroducedServiceModal.vue").default,
				props: {
					form: this.form,
					service: service,
				},
			});
		},
		viableCampaigns(service) {
			let self = this;
			return _.filter(this.form.options.campaigns, function (campaign) {
				return campaign.service.id === service.id && (
					!self.serviceProvider || campaign.introducer.id === self.serviceProvider.id
				);
			});
		},

		async submit() {
			if ("post" === this.method) {
				this.form.post("service-providers").then(response => {
					this.$router.replace({
						name: "serviceProviders.edit", params: {
							"serviceProvider": response.data.id,
						},
					});
				});
			}

			if ("patch" === this.method) {
				this.form.patch("service-providers/" + this.serviceProvider.id).then(response => {
					this.updateModel(response.data);
					this.$store.dispatch("alertsStore/success", "Service Provider Updated");
				});
			}
		},
	},

	created() {
		http().get(this.route("form-data/service-provider/:serviceProvider", this.serviceProvider)).then(response => {
			let self = this;
			let form = this.form;
			this.form.setOptions(response.data);

			this.form.pushDefault("fees.conveyancing.sale.fee_transaction_bands");
			this.form.pushDefault("fees.conveyancing.purchase.fee_transaction_bands");
			this.form.pushDefault("fees.conveyancing.remortgage.fee_transaction_bands");

			this.form.set("linked_postcode_geometries", []);

			if (this.serviceProvider) {
				this.form.prefill(this.serviceProvider);

				if (this.serviceProvider.group) {
					this.form.set("group", this.serviceProvider.group.id);
				}

				if (this.serviceProvider.introducer_commission_percent) {
					this.form.set("introducer_commission_percent", this.serviceProvider.introducer_commission_percent.percent);
				}

				if (!this.serviceProvider.profile.short_name) {
					this.setShortName(this.serviceProvider.profile.name);
				}

				if (!this.serviceProvider.slug) {
					this.setSlug(this.serviceProvider.profile.name, true);
				}

				_.each(this.serviceProvider.fees, function (fees, serviceKey) {
					fees = fees.introducers[""].fees;
					let mappedFees = _.mapValues(fees, function (fees, type) {
						return _.mapValues(fees, function (data, fee) {
							if ("fee_transaction_bands" === fee) {
								if (!data.length) {
									return form.originalData.fees.conveyancing[type].fee_transaction_bands;
								}
								return _.map(data, function (data) {
									return {
										fee: data.fee.float,
										upto: !data.upto ? null : data.upto.wholes / 1000,
									};
								});
							}

							return data ? data.float : null;
						});
					});

					_.each(mappedFees, function (mapped, type) {
						form.set("fees." + serviceKey + "." + type, mapped);
					});
				});

				this.form.set("provided_services", _.mapValues(this.serviceProvider.providedServices, function (service) {
					return this.getProvidedServiceConfig(service);
				}.bind(this)));
				console.log("sort intro data");
				let d = _.mapValues(this.serviceProvider.referredServices, service => {
					return this.getIntroducedServiceConfig(service);
				});
				console.log("formdata", this.serviceProvider.referredServices, d);
				this.form.set("introduced_services", _.mapValues(this.serviceProvider.referredServices, service => {
					return this.getIntroducedServiceConfig(service);
				}));

				this.form.set("lenders", _.map(this.serviceProvider.lenders, function (lender) {
					return lender.id;
				}));

				this.form.set("linked_postcode_geometries", _.map(this.serviceProvider.linkedPostcodeGeometries, function (geometry) {
					return geometry.area + geometry.district;
				}));

				if (this.serviceProvider?.config.comparisons) {
					this.form.set("config.comparisons", this.serviceProvider?.config.comparisons);
				}

				if (!this.form.data.admin) {
					this.form.set("admin", this.form.originalData.admin);
				}
			}

			if (this.$route.query.invitation) {
				http().get("invitations/" + this.$route.query.invitation).then(response => {
					let invitation = response.data.data;
					form.set("type", invitation.service_provider_type);
					form.set("profile.name", invitation.service_provider_name);
					form.set("admin.email", invitation.contact_email.formatted);
					form.set("admin.profile.name_first", invitation.contact_name_first);
					form.set("admin.profile.name_last", invitation.contact_name_last);
					form.set("admin.profile.phone", invitation.contact_phone);

					this.formReady = true;
				});
			} else {
				this.formReady = true;
			}
		});
	},
};
</script>


